import React, { useEffect, useState } from 'react';
import API_URL from '../config';
import { showError, showSuccess } from '../utils/toast';
import ModoToggle from './ModoToggle';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';
import './NCImportReview.css';

/**
 * v4.3.0 Feature 1 — Tela de revisão dos campos extraídos pela IA antes de criar a NC.
 *
 * Props:
 *   extractedData  — { dados_extraidos, confianca_extracao, campos_com_duvida, ... }
 *   originalFile   — File original (re-anexado como evidência se checkbox marcado)
 *   clienteId      — client_id do usuário autenticado
 *   onCancel       — callback ao cancelar (volta para NCForm vazio)
 *   onCreated      — callback(result) após POST /processar-completa retornar 200/202
 */
const SESSION_KEY = 'nc_import_draft';

function NCImportReview({ extractedData, originalFile, clienteId, onCancel, onCreated }) {
  const initialData = extractedData?.dados_extraidos || {};
  const camposDuvida = (extractedData?.campos_com_duvida || []).map((c) => c.campo);
  const motivosDuvida = Object.fromEntries(
    (extractedData?.campos_com_duvida || []).map((c) => [c.campo, c.motivo])
  );
  const confianca = extractedData?.confianca_extracao || 'baixa';
  const arquivoNome = extractedData?.arquivo_original_nome || originalFile?.name || '';

  const [form, setForm] = useState({
    produto: initialData.produto || '',
    lote: initialData.lote || '',
    data_ocorrencia: initialData.data_ocorrencia || '',
    descricao_nc: initialData.descricao_nc || '',
    origem: initialData.origem || '',
    quantidade_afetada: initialData.quantidade_afetada || '',
    cliente_emissor: initialData.cliente_emissor || '',
    numero_nc_cliente: initialData.numero_nc_cliente || '',
    prazo_resposta_cliente: initialData.prazo_resposta_cliente || '',
    contato_cliente_email: initialData.contato_cliente_email || '',
    // TD-138: contexto técnico — PDF do cliente raramente carrega esses dados
    // internos do fornecedor (linha/lote/turno/operador/aplicação no cliente).
    // Sem esses campos a IA analisa o caso especulativamente. Inicializam
    // vazios e o usuário preenche manualmente com o que sabe da operação.
    contexto_processo: '',
    contexto_setorial_custom: '',
  });
  const [anexarOriginal, setAnexarOriginal] = useState(true); // marcado por padrão
  const [submitting, setSubmitting] = useState(false);
  // Opção 4 (Sessão 17): escolha de modo ANTES da análise. Default 'registro'
  // espelha NCForm.jsx L72 — não-regressão UX. Caminho fresh investigação
  // dispara _build_prompt_v42 modo='investigacao' direto (sem upgrade pós-registro).
  const [modoAnalise, setModoAnalise] = useState('registro');

  // Limpa sessionStorage se a tela for desmontada após sucesso
  useEffect(() => () => {}, []);

  const handleChange = (campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  };

  const isCampoDuvida = (nome) => camposDuvida.includes(nome);

  const handleConfirmar = async () => {
    if (!form.descricao_nc.trim()) {
      showError('Descrição da NC é obrigatória.');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('cliente_id', String(clienteId));
      fd.append('descricao_nc', form.descricao_nc);
      if (form.produto) fd.append('produto', form.produto);
      if (form.lote) fd.append('lote', form.lote);
      if (form.data_ocorrencia) fd.append('data_ocorrencia', form.data_ocorrencia);
      if (form.origem) fd.append('origem', form.origem);
      if (form.quantidade_afetada) fd.append('quantidade_afetada', form.quantidade_afetada);

      // TD-138: contexto técnico preenchido manualmente pelo usuário pós-importação.
      // Backend canônico (_build_prompt_v42) consome ambos os campos no prompt da
      // IA — quanto mais preenchidos, mais assertiva a análise da causa raiz.
      // Endpoint /processar-completa aceita ambos como Form opcionais
      // (nc_routes.py:1392, 1398).
      if (form.contexto_processo) fd.append('contexto_processo', form.contexto_processo);
      if (form.contexto_setorial_custom) fd.append('contexto_setorial_custom', form.contexto_setorial_custom);

      // Opção 4 (Sessão 17): modo escolhido pelo usuário antes da análise.
      // Sempre truthy ('registro'|'investigacao') — append incondicional
      // espelha NCForm.jsx L138.
      fd.append('modo_analise', modoAnalise);

      // 6 campos novos da v4.3.0 Feature 1
      fd.append('origem_tipo', 'cliente');
      if (form.cliente_emissor) fd.append('cliente_emissor', form.cliente_emissor);
      if (form.numero_nc_cliente) fd.append('numero_nc_cliente', form.numero_nc_cliente);
      if (form.prazo_resposta_cliente) fd.append('prazo_resposta_cliente', form.prazo_resposta_cliente);
      if (form.contato_cliente_email) fd.append('contato_cliente_email', form.contato_cliente_email);
      if (arquivoNome) fd.append('arquivo_original_nome', arquivoNome);

      // Anexar arquivo original como evidência (se checkbox marcado)
      if (anexarOriginal && originalFile) {
        fd.append('evidencias', originalFile);
      }

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/nc/processar-completa`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showError(err.detail || `Erro ${res.status} ao criar NC`);
        setSubmitting(false);
        return;
      }

      const result = await res.json();
      // Limpa sessionStorage só após sucesso
      sessionStorage.removeItem(SESSION_KEY);
      showSuccess('NC criada e enviada para análise.');
      if (onCreated) onCreated(result);
    } catch (err) {
      showError(`Falha de conexão: ${err.message}`);
      setSubmitting(false);
    }
  };

  const bannerClass = `nc-import-review-banner conf-${confianca}`;
  // Caminho (c) — a IA não calibra de forma confiável a própria confiança da extração
  // (#184: mesmo PDF → saídas diferentes), e o default-ausente caía em 'baixa' ⇒ o
  // alarme vermelho "incerta" tocava quase sempre e virava paisagem. 'baixa' agora é uma
  // mensagem NEUTRA "confira os campos" (sem carga de alarme). Os destaques amarelos
  // por-campo (campos_com_duvida) — esses sim específicos e úteis — continuam.
  const bannerText = {
    alta: 'Extração de alta confiança. Revise e confirme.',
    media: 'Revise os campos destacados em amarelo.',
    baixa: 'Confira os campos extraídos antes de criar a NC — preencha o que estiver vazio ou ambíguo.',
  }[confianca] || '';
  const bannerIcon = {
    alta: 'check_circle',
    media: 'warning',
    baixa: 'fact_check',
  }[confianca];

  return (
    <div className="nc-import-review-container">
      <PageHeader
        title="Revisar NC importada"
        subtitle="Confira os campos extraídos pela IA antes de criar a NC."
      />

      <div className={bannerClass}>
        {bannerIcon && (
          <span className="material-symbols-outlined nc-import-review-banner-icon">{bannerIcon}</span>
        )}
        {bannerText}
      </div>

      {/*
        TD-138: banner amarelo de alerta quando contexto técnico não foi
        preenchido. PDF do cliente raramente carrega esses dados (linha/
        lote/turno/operador/aplicação no cliente) — sem eles, a IA roda
        com fallback "Não informado" e gera análise especulativa.
        Banner desaparece quando QUALQUER um dos 2 campos é preenchido.
        Submit continua habilitado (decisão produto: honesto sem ser
        bloqueante — coerente com TD-146).
      */}
      {form.contexto_processo === '' && form.contexto_setorial_custom === '' && (
        <div style={{
          padding: '12px 16px',
          margin: '12px 0',
          background: '#fff8e1',
          border: '1px solid #ffd54f',
          borderLeft: '4px solid #ffa726',
          borderRadius: '4px',
          color: '#5d4e00',
          fontSize: '13px',
          lineHeight: '1.5',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'text-bottom' }}>warning</span>{' '}
          <strong>Sem contexto técnico, a análise da IA será especulativa.</strong>{' '}
          Recomendamos preencher os campos abaixo com o que você sabe da operação.
        </div>
      )}

      {arquivoNome && (
        <div className="nc-import-review-file">
          <small>Arquivo original: <strong>{arquivoNome}</strong></small>
        </div>
      )}

      <div className="nc-import-review-card">
        <h4>Dados do cliente emissor</h4>
        <div className="nc-import-review-grid">
          <Field
            label="Cliente emissor"
            value={form.cliente_emissor}
            onChange={(v) => handleChange('cliente_emissor', v)}
            duvida={isCampoDuvida('cliente_emissor')}
            motivo={motivosDuvida.cliente_emissor}
            placeholder="Nome da empresa que enviou a NC"
          />
          <Field
            label="Número da NC no cliente"
            value={form.numero_nc_cliente}
            onChange={(v) => handleChange('numero_nc_cliente', v)}
            duvida={isCampoDuvida('numero_nc_cliente')}
            motivo={motivosDuvida.numero_nc_cliente}
          />
          <Field
            label="Prazo de resposta"
            type="date"
            value={form.prazo_resposta_cliente}
            onChange={(v) => handleChange('prazo_resposta_cliente', v)}
            duvida={isCampoDuvida('prazo_resposta_cliente')}
            motivo={motivosDuvida.prazo_resposta_cliente}
          />
          <Field
            label="Email de contato"
            type="email"
            value={form.contato_cliente_email}
            onChange={(v) => handleChange('contato_cliente_email', v)}
            duvida={isCampoDuvida('contato_cliente_email')}
            motivo={motivosDuvida.contato_cliente_email}
          />
        </div>

        <h4>Dados da não-conformidade</h4>
        <div className="nc-import-review-grid">
          <Field
            label="Produto"
            value={form.produto}
            onChange={(v) => handleChange('produto', v)}
            duvida={isCampoDuvida('produto')}
            motivo={motivosDuvida.produto}
          />
          <Field
            label="Lote"
            value={form.lote}
            onChange={(v) => handleChange('lote', v)}
            duvida={isCampoDuvida('lote')}
            motivo={motivosDuvida.lote}
          />
          <Field
            label="Data da ocorrência"
            type="date"
            value={form.data_ocorrencia}
            onChange={(v) => handleChange('data_ocorrencia', v)}
            duvida={isCampoDuvida('data_ocorrencia')}
            motivo={motivosDuvida.data_ocorrencia}
          />
          <Field
            label="Origem (linha/setor)"
            value={form.origem}
            onChange={(v) => handleChange('origem', v)}
            duvida={isCampoDuvida('origem')}
            motivo={motivosDuvida.origem}
          />
          <Field
            label="Quantidade afetada"
            value={form.quantidade_afetada}
            onChange={(v) => handleChange('quantidade_afetada', v)}
            duvida={isCampoDuvida('quantidade_afetada')}
            motivo={motivosDuvida.quantidade_afetada}
          />
        </div>

        <div className="nc-import-review-textarea-wrap">
          <label>Descrição da NC <span className="required">*</span></label>
          {isCampoDuvida('descricao_nc') && (
            <small className="duvida-motivo">
              <span className="material-symbols-outlined duvida-motivo-icon">warning</span>
              {motivosDuvida.descricao_nc}
            </small>
          )}
          <textarea
            value={form.descricao_nc}
            onChange={(e) => handleChange('descricao_nc', e.target.value)}
            rows={5}
            className={isCampoDuvida('descricao_nc') ? 'campo-duvida' : ''}
          />
        </div>

        {/*
          TD-138: contexto técnico que o PDF do cliente raramente carrega.
          Usuário preenche manualmente com o que sabe da operação interna.
          Mesmo placeholders ricos do NCForm.jsx pra paridade visual entre
          caminho manual e caminho importação.
        */}
        <h4>Contexto técnico (recomendado)</h4>
        <div className="nc-import-review-textarea-wrap">
          <label>Situação atual do processo</label>
          <textarea
            value={form.contexto_processo}
            onChange={(e) => handleChange('contexto_processo', e.target.value)}
            rows={4}
            placeholder="Ex: a peça é cortada numa máquina semiautomática; existe instrução de trabalho impressa no posto, mas nem sempre é seguida na correria; o operador entrou há 1 mês. Conte o seu caso assim — com o máximo de detalhe que puder."
          />
          <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
            Conte como o seu processo funciona e o que pode ter influenciado o problema. É o campo que mais melhora a análise — você revisa o resultado depois.
          </small>
          {/* Coaching de contexto REFORÇADO (Francis 15/06) — concreto pra leigo +
              a IA analisa SÓ com o que for informado. Simetria com AvulsaFillForm
              e NCForm. Substitui o antigo helper 6M (mais abstrato). */}
          <div style={{
            marginTop: '8px',
            padding: '10px 12px',
            background: '#f0f7ff',
            borderLeft: '3px solid #4a90e2',
            borderRadius: '3px',
            fontSize: '12px',
            color: '#2c5282',
            lineHeight: '1.6',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'text-bottom' }}>lightbulb</span>{' '}
            <strong>O que ajuda a IA a achar a causa — descreva o que souber:</strong>
            <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
              <li>Como o processo funciona, na prática (do começo ao fim do que deu errado)</li>
              <li>Se a etapa é feita por máquinas/equipamentos ou à mão, por uma pessoa (ou os dois)</li>
              <li>Se existe uma instrução de trabalho / procedimento escrito para essa etapa — ou se ela é feita "de cabeça"</li>
              <li>Se essa instrução é seguida no dia a dia — ou se costuma ter atalho/improviso</li>
              <li>Se quem faz é treinado e/ou experiente (ex: há quanto tempo na função, recebeu treinamento)</li>
              <li>Tudo o que pode ter influenciado: troca de turno, pressa, equipamento parado, material diferente, mudança recente no processo</li>
            </ul>
          </div>
          <div style={{
            marginTop: '8px',
            padding: '10px 12px',
            background: '#fffbeb',
            borderLeft: '3px solid #f59e0b',
            borderRadius: '3px',
            fontSize: '12px',
            color: '#92400e',
            lineHeight: '1.6',
          }}>
            A IA analisa <strong>apenas com as informações que você escrever aqui</strong> — ela não conhece a sua
            empresa nem visita o seu processo. Por isso, quanto mais detalhe você der, melhor e mais certeira fica a
            análise. E se você já desconfia da causa, diga aqui — a IA usa essa pista, sem ficar presa a ela.
          </div>
        </div>

        <div className="nc-import-review-textarea-wrap">
          <label>Contexto da Aplicação no Cliente (Opcional)</label>
          <textarea
            value={form.contexto_setorial_custom}
            onChange={(e) => handleChange('contexto_setorial_custom', e.target.value)}
            rows={2}
            placeholder="Como o seu produto é usado pelo cliente e qual o risco da NC para ele. Ex: vedação de compressor de refrigerador doméstico, exige resistência a -20°C, norma ASTM D2000, cliente com certificação IATF."
          />
          <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
            Descreva a <strong>aplicação no cliente</strong> — criticidade, normas exigidas, riscos. Ajuda a IA a dimensionar o impacto e priorizar ações.
          </small>
        </div>

        <label className="nc-import-review-checkbox">
          <input
            type="checkbox"
            checked={anexarOriginal}
            onChange={(e) => setAnexarOriginal(e.target.checked)}
          />
          <span>
            <strong>Anexar arquivo original como evidência</strong>
            <small>O arquivo enviado fica visível no histórico da NC. Desmarque se contém dados sensíveis que não devem ficar registrados.</small>
          </span>
        </label>
      </div>

      <ModoToggle value={modoAnalise} onChange={setModoAnalise} disabled={submitting} />

      <div className="nc-import-review-actions">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmar}
          disabled={submitting}
          icon={
            submitting
              ? (modoAnalise === 'investigacao' ? 'science' : 'hourglass_empty')
              : (modoAnalise === 'investigacao' ? 'science' : 'check_circle')
          }
        >
          {submitting
            ? (modoAnalise === 'investigacao' ? 'Investigando (~40s)…' : 'Criando NC...')
            : (modoAnalise === 'investigacao' ? 'Confirmar e Investigar' : 'Confirmar e analisar')}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, type = 'text', value, onChange, duvida, motivo, placeholder }) {
  return (
    <div className="nc-import-review-field">
      <label>{label}</label>
      {duvida && motivo && (
        <small className="duvida-motivo">
          <span className="material-symbols-outlined duvida-motivo-icon">warning</span>
          {motivo}
        </small>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={duvida ? 'campo-duvida' : ''}
      />
    </div>
  );
}

export default NCImportReview;
